/** Stateful critical-lab-value acknowledgment lifecycle — backs /lab/critical-alerts/... */
import { fetchApi } from '../client';

/** One alert as GET /lab/critical-alerts returns it (LabCriticalAlertView): the alert's own fields plus the patient,
 *  test and value, and the ordering (or referring) doctor with their phone — any of these may be null. */
export interface LabCriticalAlertView {
  id: string;
  status: string;
  labOrderId?: string | null;
  patientId?: string | null;
  detectedAt?: string | null;
  notifiedAt?: string | null;
  communicationNotes?: string | null;
  patientName?: string | null;
  testName?: string | null;
  parameterName?: string | null;
  resultValue?: string | null;
  unit?: string | null;
  doctorName?: string | null;
  doctorPhone?: string | null;
}

/** The server's limit for a resolve note. */
export const CRITICAL_RESOLVE_NOTE_MAX = 1000;

export const labCriticalAlertsModule = {
  list: (status?: string[]) => {
    const qs = status && status.length ? `?${status.map(s => `status=${s}`).join('&')}` : '';
    return fetchApi<LabCriticalAlertView[]>(`/api/lab/critical-alerts${qs}`);
  },

  /** DOCTOR / LAB_ADMIN / CLINIC_ADMIN; ACKNOWLEDGED or ESCALATED alerts. An optional note of what was done is kept on
   *  the alert and in its audit. */
  resolve: (alertId: string, note?: string) => {
    const n = note?.trim();
    return fetchApi<LabCriticalAlertView>(`/api/lab/critical-alerts/${alertId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(n ? { note: n.slice(0, CRITICAL_RESOLVE_NOTE_MAX) } : {}),
    });
  },

  acknowledge: (alertId: string) =>
    fetchApi<any>(`/api/lab/critical-alerts/${alertId}/acknowledge`, { method: 'POST' }),

  /** Active doctors of this clinic the alert can go to; the one the auto-escalation policy would pick is first
   *  (suggested: true). */
  escalationTargets: (alertId: string) =>
    fetchApi<Array<{ userId: string; name: string; suggested: boolean }>>(
      `/api/lab/critical-alerts/${alertId}/escalation-targets`),

  /** escalateToUserId omitted -> the server picks the target by the same policy as auto-escalation. */
  escalate: (alertId: string, escalateToUserId?: string) =>
    fetchApi<any>(`/api/lab/critical-alerts/${alertId}/escalate`, {
      method: 'POST',
      body: JSON.stringify({ escalateToUserId }),
    }),
};
